import { colToLetter } from '../utils/columnUtils'
import type {
  SpreadsheetFormulaAuthoringAnalysis,
  SpreadsheetFormulaAuthoringReferenceKind,
  SpreadsheetFormulaAuthoringSpan,
} from '../protocol/spreadsheetEngineProtocol'
import { FORMULA_FUNCTION_SIGNATURES, formulaFunctionByName, type FormulaFunctionSignature } from './functionCatalog'
export { FORMULA_FUNCTION_SIGNATURES, type FormulaFunctionSignature } from './functionCatalog'

export type FormulaEditMode = 'value' | 'edit' | 'point' | 'range-resize' | 'function-assist'

export type FormulaCommitNavigation = 'down' | 'up' | 'right' | 'left' | 'none'

export type FormulaCellCoord = {
  col: number
  row: number
}

export type FormulaRangeCoord = {
  sheetName?: string
  start: FormulaCellCoord
  end: FormulaCellCoord
}

export type FormulaReferenceKind = 'cell' | 'range' | 'whole-column' | 'whole-row' | 'spill'

export type FormulaReferenceSpan = {
  id: string
  kind: FormulaReferenceKind
  text: string
  startOffset: number
  endOffset: number
  sheetName?: string
  range?: FormulaRangeCoord
  colorIndex: number
}

export type FormulaFunctionSpan = {
  name: string
  startOffset: number
  endOffset: number
  openParenOffset: number
}

export type FormulaNamedReferenceSpan = {
  name: string
  startOffset: number
  endOffset: number
}

export type FormulaAuthoringTextAnalysis = {
  referenceSpans: FormulaReferenceSpan[]
  functionSpans: FormulaFunctionSpan[]
  namedReferenceSpans: FormulaNamedReferenceSpan[]
  parseError: { offset: number; message: string } | null
}

export type FormulaAuthoringParser = (text: string) => SpreadsheetFormulaAuthoringAnalysis | null

export type FormulaEditSession = {
  id: string
  anchorCell: FormulaCellCoord
  anchorSheetName?: string
  activeSheetName?: string
  originalText: string
  draft: string
  caretStart: number
  caretEnd: number
  mode: FormulaEditMode
  activeReferenceId: string | null
  pointRange: FormulaRangeCoord | null
  referenceSpans: FormulaReferenceSpan[]
  functionSpans: FormulaFunctionSpan[]
  namedReferenceSpans: FormulaNamedReferenceSpan[]
  parseError: { offset: number; message: string } | null
  functionSuggestionIndex: number
  commitNavigation: FormulaCommitNavigation | null
  status: 'active' | 'committed' | 'cancelled'
}

export type FormulaKeyCommand = {
  key: string
  shiftKey?: boolean
  ctrlKey?: boolean
  metaKey?: boolean
  altKey?: boolean
}

export type FormulaKeyCommandOptions = {
  autoSumRange?: FormulaRangeCoord | null
}

export type FormulaKeyCommandDisposition = 'formula-command' | 'native-editing' | 'host-suppressed' | 'unhandled'

export type FormulaKeyCommandDecision = {
  commandId: string
  disposition: FormulaKeyCommandDisposition
  preventDefault: boolean
  stopPropagation: boolean
}

export type FormulaArrowDirection = 'up' | 'down' | 'left' | 'right'

export type FormulaReferenceAbsoluteMode = 'relative' | 'absolute' | 'row-absolute' | 'col-absolute'

export type FormulaTextRun = {
  text: string
  color?: string
  referenceId?: string
  namedReference?: string
  active?: boolean
}

export type FormulaFunctionAssistance = {
  autocompleteQuery: string | null
  suggestions: FormulaFunctionSignature[]
  activeSuggestionIndex: number
  activeFunction: FormulaFunctionSignature | null
  activeArgumentIndex: number | null
  activeArgumentDetails: string[]
}

export type FormulaFunctionInsertionOptions = {
  includeClosingParen?: boolean
  includeArgumentPlaceholders?: boolean
}

export type FormulaFunctionEditTarget = {
  name: string
  args: string[]
  startOffset: number
  endOffset: number
}

export type FormulaFunctionInsertionDraft = {
  draft: string
  caretStart: number
  caretEnd: number
}

let sharedFormulaEditSession: FormulaEditSession | null = null
let formulaAuthoringParser: FormulaAuthoringParser | null = null

const FORMULA_REF_COLORS = [
  '#2563eb',
  '#dc2626',
  '#16a34a',
  '#9333ea',
  '#ea580c',
  '#0891b2',
  '#c026d3',
  '#4f46e5',
] as const

const emptyAuthoringAnalysis: FormulaAuthoringTextAnalysis = {
  referenceSpans: [],
  functionSpans: [],
  namedReferenceSpans: [],
  parseError: null,
}

const clampNonNegativeInteger = (value: number): number => (
  Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0
)

const clampFormulaCaretOffset = (value: number, draft: string): number =>
  Math.min(draft.length, clampNonNegativeInteger(value))

const normalizeSheetName = (sheetName: string | null | undefined): string | undefined => {
  const trimmed = String(sheetName ?? '').trim()
  return trimmed || undefined
}

const sheetNameKey = (sheetName: string | null | undefined): string =>
  normalizeSheetName(sheetName)?.toLowerCase() ?? ''

const normalizeCellCoord = (coord: FormulaCellCoord): FormulaCellCoord => ({
  col: clampNonNegativeInteger(coord.col),
  row: clampNonNegativeInteger(coord.row),
})

export const normalizeFormulaRange = (range: FormulaRangeCoord): FormulaRangeCoord => {
  const start = normalizeCellCoord(range.start)
  const end = normalizeCellCoord(range.end)
  return {
    sheetName: range.sheetName?.trim() || undefined,
    start: {
      col: Math.min(start.col, end.col),
      row: Math.min(start.row, end.row),
    },
    end: {
      col: Math.max(start.col, end.col),
      row: Math.max(start.row, end.row),
    },
  }
}

export const publishSharedFormulaEditSession = (session: FormulaEditSession | null): void => {
  sharedFormulaEditSession = session && session.status === 'active' ? { ...session } : null
}

export const getSharedFormulaEditSession = (): FormulaEditSession | null => (
  sharedFormulaEditSession ? { ...sharedFormulaEditSession } : null
)

export const clearSharedFormulaEditSession = (): void => {
  sharedFormulaEditSession = null
}

export const resumeFormulaEditSessionForSheet = (
  session: FormulaEditSession,
  activeSheetName: string | null | undefined,
): FormulaEditSession => ({
  ...session,
  activeSheetName: normalizeSheetName(activeSheetName),
  status: 'active',
})

export const qualifyFormulaRangeForActiveSheet = (
  session: FormulaEditSession,
  range: FormulaRangeCoord,
  activeSheetName: string | null | undefined,
): FormulaRangeCoord => {
  const currentSheetName = normalizeSheetName(activeSheetName)
  const anchorSheetName = normalizeSheetName(session.anchorSheetName)
  if (!currentSheetName || !anchorSheetName || sheetNameKey(currentSheetName) === sheetNameKey(anchorSheetName)) {
    return { ...range, sheetName: undefined }
  }
  return { ...range, sheetName: currentSheetName }
}

export const isFormulaReferenceVisibleOnSheet = (
  session: FormulaEditSession,
  span: FormulaReferenceSpan,
  activeSheetName: string | null | undefined,
): boolean => {
  if (!span.range) return false
  const currentSheetKey = sheetNameKey(activeSheetName)
  if (!currentSheetKey) return !span.sheetName
  const targetSheetName = normalizeSheetName(span.sheetName) ?? normalizeSheetName(session.anchorSheetName)
  return sheetNameKey(targetSheetName) === currentSheetKey
}

export const canCommitFormulaSessionOnSheet = (
  session: FormulaEditSession | null | undefined,
  activeSheetName: string | null | undefined,
): boolean => {
  if (!session?.anchorSheetName) return true
  if (!activeSheetName) return false
  return sheetNameKey(session.anchorSheetName) === sheetNameKey(activeSheetName)
}

export const isActiveFormulaReferenceEditSession = (
  session: FormulaEditSession | null | undefined,
): session is FormulaEditSession => (
  Boolean(session && session.status === 'active' && session.mode !== 'value')
)

export const getFormulaReferenceColor = (colorIndex: number): string =>
  FORMULA_REF_COLORS[Math.abs(colorIndex) % FORMULA_REF_COLORS.length]

export const formatFormulaCellRef = (
  coord: FormulaCellCoord,
  mode: FormulaReferenceAbsoluteMode = 'relative',
): string => {
  const cell = normalizeCellCoord(coord)
  const colAbs = mode === 'absolute' || mode === 'col-absolute'
  const rowAbs = mode === 'absolute' || mode === 'row-absolute'
  return `${colAbs ? '$' : ''}${colToLetter(cell.col)}${rowAbs ? '$' : ''}${cell.row + 1}`
}

const quoteSheetName = (sheetName: string): string => {
  const trimmed = sheetName.trim()
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(trimmed)) return trimmed
  return `'${trimmed.replace(/'/g, "''")}'`
}

export const formatFormulaRangeRef = (range: FormulaRangeCoord): string => {
  const normalized = normalizeFormulaRange(range)
  const prefix = normalized.sheetName ? `${quoteSheetName(normalized.sheetName)}!` : ''
  const start = formatFormulaCellRef(normalized.start)
  const end = formatFormulaCellRef(normalized.end)
  return normalized.start.col === normalized.end.col && normalized.start.row === normalized.end.row
    ? `${prefix}${start}`
    : `${prefix}${start}:${end}`
}

const columnIndexFromLetters = (letters: string): number => {
  let value = 0
  for (const char of letters.toUpperCase()) {
    const code = char.charCodeAt(0)
    if (code < 65 || code > 90) return -1
    value = value * 26 + (code - 64)
  }
  return value - 1
}

const isFormulaText = (text: string): boolean => text.trimStart().startsWith('=')

export const configureFormulaAuthoringParser = (parser: FormulaAuthoringParser | null): void => {
  formulaAuthoringParser = parser
}

const mapFormulaReferenceKind = (
  kind: SpreadsheetFormulaAuthoringReferenceKind,
): FormulaReferenceKind => {
  switch (kind) {
    case 'cell': return 'cell'
    case 'range': return 'range'
    case 'whole_column': return 'whole-column'
    case 'whole_row': return 'whole-row'
    case 'spill': return 'spill'
  }
}

const normalizeAuthoringRangeEnd = (
  value: number,
  kind: FormulaReferenceKind,
  axis: 'row' | 'col',
): number => {
  if (kind === 'whole-column' && axis === 'row') return Number.MAX_SAFE_INTEGER
  if (kind === 'whole-row' && axis === 'col') return Number.MAX_SAFE_INTEGER
  return clampNonNegativeInteger(value)
}

const formulaReferenceSpanFromAuthoringSpan = (
  span: Extract<SpreadsheetFormulaAuthoringSpan, { kind: 'reference' }>,
  colorIndex: number,
): FormulaReferenceSpan => {
  const kind = mapFormulaReferenceKind(span.reference_kind)
  const sheetName = normalizeSheetName(span.sheet_name ?? span.range.sheet_name)
  return {
    id: `ref_${colorIndex}`,
    kind,
    text: span.text,
    startOffset: clampNonNegativeInteger(span.start_offset),
    endOffset: clampNonNegativeInteger(span.end_offset),
    sheetName,
    range: normalizeFormulaRange({
      sheetName,
      start: {
        col: clampNonNegativeInteger(span.range.start.col),
        row: clampNonNegativeInteger(span.range.start.row),
      },
      end: {
        col: normalizeAuthoringRangeEnd(span.range.end.col, kind, 'col'),
        row: normalizeAuthoringRangeEnd(span.range.end.row, kind, 'row'),
      },
    }),
    colorIndex,
  }
}

export const analyzeFormulaAuthoringText = (text: string): FormulaAuthoringTextAnalysis => {
  if (!isFormulaText(text) || !formulaAuthoringParser) return emptyAuthoringAnalysis
  const analysis = formulaAuthoringParser(text)
  if (!analysis) return emptyAuthoringAnalysis
  const referenceSpans = analysis.spans
    .filter((span): span is Extract<SpreadsheetFormulaAuthoringSpan, { kind: 'reference' }> => span.kind === 'reference')
    .map((span, index) => formulaReferenceSpanFromAuthoringSpan(span, index))
  const functionSpans = analysis.spans
    .filter((span): span is Extract<SpreadsheetFormulaAuthoringSpan, { kind: 'function' }> => span.kind === 'function')
    .map((span) => ({
      name: span.name,
      startOffset: clampNonNegativeInteger(span.start_offset),
      endOffset: clampNonNegativeInteger(span.end_offset),
      openParenOffset: clampNonNegativeInteger(span.open_paren_offset),
    }))
  const namedReferenceSpans = analysis.spans
    .filter((span): span is Extract<SpreadsheetFormulaAuthoringSpan, { kind: 'named_reference' }> => span.kind === 'named_reference')
    .map((span) => ({
      name: span.name,
      startOffset: clampNonNegativeInteger(span.start_offset),
      endOffset: clampNonNegativeInteger(span.end_offset),
    }))
  return {
    referenceSpans,
    functionSpans,
    namedReferenceSpans,
    parseError: analysis.parse_error
      ? {
        offset: clampNonNegativeInteger(analysis.parse_error.offset),
        message: analysis.parse_error.message,
      }
      : null,
  }
}

export const extractFormulaReferenceSpans = (text: string): FormulaReferenceSpan[] =>
  analyzeFormulaAuthoringText(text).referenceSpans

export const buildFormulaTextRuns = (
  text: string,
  referenceSpans: FormulaReferenceSpan[],
  activeReferenceId: string | null = null,
  namedReferenceSpans: FormulaNamedReferenceSpan[] = [],
): FormulaTextRun[] => {
  const runs: FormulaTextRun[] = []
  let offset = 0
  const spans = [
    ...referenceSpans.map((span) => ({ kind: 'reference' as const, span })),
    ...namedReferenceSpans.map((span) => ({ kind: 'named' as const, span })),
  ]
    .sort((left, right) => left.span.startOffset - right.span.startOffset)
    .filter(({ span }) => span.startOffset >= 0 && span.endOffset > span.startOffset && span.endOffset <= text.length)

  for (const { kind, span } of spans) {
    if (span.startOffset < offset) continue
    if (span.startOffset > offset) {
      runs.push({ text: text.slice(offset, span.startOffset) })
    }
    if (kind === 'reference') {
      runs.push({
        text: text.slice(span.startOffset, span.endOffset),
        color: getFormulaReferenceColor(span.colorIndex),
        referenceId: span.id,
        active: span.id === activeReferenceId,
      })
    } else {
      runs.push({
        text: text.slice(span.startOffset, span.endOffset),
        color: '#0f766e',
        namedReference: span.name,
      })
    }
    offset = span.endOffset
  }

  if (offset < text.length) {
    runs.push({ text: text.slice(offset) })
  }
  return runs.length > 0 ? runs : [{ text }]
}

const isFormulaIdentifierCharacter = (char: string | undefined): boolean => (
  Boolean(char && /[A-Za-z0-9_.]/.test(char))
)

const getFormulaAutocompleteQuery = (text: string, caretOffset: number): string | null => {
  if (!isFormulaText(text)) return null
  const before = text.slice(0, Math.max(0, caretOffset))
  const match = /([A-Za-z][A-Za-z0-9_.]*)$/.exec(before)
  if (!match) return null
  const prefixStart = before.length - match[1].length
  const previous = before.slice(0, prefixStart).trimEnd().at(-1)
  if (previous && previous !== '=' && previous !== '(' && previous !== ',' && !/[+\-*/^&=<>{}:]/.test(previous)) {
    return null
  }
  return match[1].toUpperCase()
}

const getActiveFunctionCall = (
  text: string,
  caretOffset: number,
): { name: string; argumentIndex: number } | null => {
  if (!isFormulaText(text)) return null
  const before = text.slice(0, Math.max(0, caretOffset))
  let inString = false
  const stack: Array<{ name: string; argumentIndex: number }> = []

  for (let index = 0; index < before.length; index += 1) {
    const char = before[index]
    if (char === '"') {
      if (before[index + 1] === '"') {
        index += 1
        continue
      }
      inString = !inString
      continue
    }
    if (inString) continue
    if (char === '(') {
      const nameEnd = index
      let nameStart = nameEnd - 1
      while (nameStart >= 0 && isFormulaIdentifierCharacter(before[nameStart])) {
        nameStart -= 1
      }
      const name = before.slice(nameStart + 1, nameEnd).toUpperCase()
      if (name && /^[A-Z][A-Z0-9_.]*$/.test(name)) {
        stack.push({ name, argumentIndex: 0 })
      } else {
        stack.push({ name: '', argumentIndex: 0 })
      }
      continue
    }
    if (char === ')') {
      stack.pop()
      continue
    }
    if (char === ',' && stack.length > 0) {
      stack[stack.length - 1].argumentIndex += 1
    }
  }

  return stack.filter((entry) => entry.name).at(-1) ?? null
}

export const splitFormulaArgumentText = (text: string): string[] => {
  const args: string[] = []
  let start = 0
  let depth = 0
  let inString = false
  const draft = String(text ?? '')
  for (let index = 0; index < draft.length; index += 1) {
    const char = draft[index]
    if (char === '"') {
      if (draft[index + 1] === '"') {
        index += 1
        continue
      }
      inString = !inString
      continue
    }
    if (inString) continue
    if (char === '(' || char === '{') {
      depth += 1
      continue
    }
    if ((char === ')' || char === '}') && depth > 0) {
      depth -= 1
      continue
    }
    if (char === ',' && depth === 0) {
      args.push(draft.slice(start, index).trim())
      start = index + 1
    }
  }
  const tail = draft.slice(start).trim()
  if (tail || args.length > 0) args.push(tail)
  return args
}

export const findFormulaFunctionCallAtCaret = (
  text: string,
  caretOffset: number,
): FormulaFunctionEditTarget | null => {
  const draft = String(text || '')
  if (!isFormulaText(draft)) return null
  const caret = clampFormulaCaretOffset(caretOffset, draft)
  let inString = false
  const stack: Array<{ name: string; startOffset: number; openParenOffset: number }> = []
  let candidate: FormulaFunctionEditTarget | null = null

  const complete = (
    entry: { name: string; startOffset: number; openParenOffset: number },
    endOffset: number,
  ): void => {
    if (!entry.name || caret < entry.startOffset || caret > endOffset) return
    const argsText = draft.slice(entry.openParenOffset + 1, Math.max(entry.openParenOffset + 1, endOffset - 1))
    const next = {
      name: entry.name,
      args: splitFormulaArgumentText(argsText),
      startOffset: entry.startOffset,
      endOffset,
    }
    if (!candidate || next.startOffset >= candidate.startOffset) candidate = next
  }

  for (let index = 0; index < draft.length; index += 1) {
    const char = draft[index]
    if (char === '"') {
      if (draft[index + 1] === '"') {
        index += 1
        continue
      }
      inString = !inString
      continue
    }
    if (inString) continue
    if (char === '(') {
      const nameEnd = index
      let nameStart = nameEnd - 1
      while (nameStart >= 0 && isFormulaIdentifierCharacter(draft[nameStart])) {
        nameStart -= 1
      }
      const name = draft.slice(nameStart + 1, nameEnd).toUpperCase()
      stack.push({
        name: /^[A-Z][A-Z0-9_.]*$/.test(name) ? name : '',
        startOffset: nameStart + 1,
        openParenOffset: index,
      })
    } else if (char === ')') {
      const entry = stack.pop()
      if (entry) complete(entry, index + 1)
    }
  }
  while (stack.length > 0) {
    const entry = stack.pop()
    if (entry) complete(entry, draft.length)
  }
  return candidate
}

export const buildFormulaFunctionCall = (
  functionName: string,
  args: string[],
): string | null => {
  const name = String(functionName || '').trim().toUpperCase()
  if (!name) return null
  return `${name}(${args.map((arg) => String(arg ?? '').trim()).join(', ')})`
}

export const buildFormulaFunctionInsertionDraft = (args: {
  session: FormulaEditSession | null
  functionName: string
  functionArgs: string[]
  editTarget?: FormulaFunctionEditTarget | null
}): FormulaFunctionInsertionDraft | null => {
  const call = buildFormulaFunctionCall(args.functionName, args.functionArgs)
  const session = args.session
  const editTarget = args.editTarget ?? null
  if (!call || !session || session.status !== 'active' || !editTarget) return null
  const draft = session.draft || '='
  const startOffset = Math.max(0, Math.min(editTarget.startOffset, draft.length))
  const endOffset = Math.max(startOffset, Math.min(editTarget.endOffset, draft.length))
  if (startOffset >= endOffset) return null
  const nextDraft = `${draft.slice(0, startOffset)}${call}${draft.slice(endOffset)}`
  const nextCaret = startOffset + call.length
  return {
    draft: nextDraft,
    caretStart: nextCaret,
    caretEnd: nextCaret,
  }
}

export const getFormulaFunctionAssistance = (
  text: string,
  caretOffset: number,
  preferredSuggestionIndex = 0,
): FormulaFunctionAssistance | null => {
  if (!isFormulaText(text)) return null
  const autocompleteQuery = getFormulaAutocompleteQuery(text, caretOffset)
  const suggestions = autocompleteQuery
    ? FORMULA_FUNCTION_SIGNATURES
      .filter((signature) => signature.name.startsWith(autocompleteQuery))
      .slice(0, 8)
    : []
  const activeCall = getActiveFunctionCall(text, caretOffset)
  const activeFunction = activeCall ? formulaFunctionByName.get(activeCall.name) ?? null : null
  const activeArgumentIndex = activeCall && activeFunction ? activeCall.argumentIndex : null

  if (!autocompleteQuery && !activeFunction) return null
  const activeSuggestionIndex = suggestions.length > 0
    ? Math.min(Math.max(0, Math.floor(preferredSuggestionIndex)), suggestions.length - 1)
    : 0
  return {
    autocompleteQuery,
    suggestions,
    activeSuggestionIndex,
    activeFunction,
    activeArgumentIndex,
    activeArgumentDetails: activeFunction && activeArgumentIndex != null
      ? activeFunction.argumentDetails?.[activeArgumentIndex] ?? []
      : [],
  }
}

const shouldInsertClosingParen = (text: string, caretOffset: number): boolean => {
  const next = text.slice(Math.max(0, caretOffset)).trimStart().at(0)
  return next !== ')'
}

export const insertFormulaFunctionSuggestion = (
  session: FormulaEditSession,
  functionName: string,
  options: FormulaFunctionInsertionOptions = {},
): FormulaEditSession => {
  const autocompleteQuery = getFormulaAutocompleteQuery(session.draft, session.caretEnd)
  if (!autocompleteQuery) return session
  const signature = formulaFunctionByName.get(functionName.toUpperCase())
  if (!signature) return session
  const startOffset = Math.max(0, session.caretEnd - autocompleteQuery.length)
  const args = signature.args.join(', ')
  const includeArgumentPlaceholders = Boolean(options.includeArgumentPlaceholders)
  const includeClosingParen = options.includeClosingParen ?? signature.args.length === 0
  const replacement = includeArgumentPlaceholders
    ? `${signature.name}(${args}${includeClosingParen ? ')' : ''}`
    : `${signature.name}(${includeClosingParen ? ')' : ''}`
  const replaced = replaceText(session.draft, startOffset, session.caretEnd, replacement)
  const caretStart = includeArgumentPlaceholders
    ? startOffset + signature.name.length + 1
    : startOffset + replacement.length - (includeClosingParen && signature.args.length > 0 ? 1 : 0)
  const caretEnd = includeArgumentPlaceholders
    ? caretStart + args.length
    : caretStart
  return {
    ...updateFormulaDraft(session, replaced.draft, caretStart, caretEnd),
    mode: signature.args.length > 0 && !includeArgumentPlaceholders ? 'point' : 'function-assist',
    functionSuggestionIndex: 0,
  }
}

export const moveFormulaFunctionSuggestion = (
  session: FormulaEditSession,
  direction: 'previous' | 'next',
): FormulaEditSession => {
  const assistance = getFormulaFunctionAssistance(session.draft, session.caretEnd, session.functionSuggestionIndex)
  if (!assistance?.autocompleteQuery || assistance.suggestions.length <= 1) return session
  const delta = direction === 'next' ? 1 : -1
  const count = assistance.suggestions.length
  return {
    ...session,
    mode: 'function-assist',
    functionSuggestionIndex: (assistance.activeSuggestionIndex + delta + count) % count,
  }
}

const insertFormulaFunctionArgumentPlaceholders = (
  session: FormulaEditSession,
): FormulaEditSession => {
  const assistance = getFormulaFunctionAssistance(session.draft, session.caretEnd, session.functionSuggestionIndex)
  const signature = assistance?.activeFunction
    ?? assistance?.suggestions[assistance.activeSuggestionIndex]
    ?? null
  if (!signature) return session

  const args = signature.args.join(', ')
  if (assistance?.autocompleteQuery) {
    return insertFormulaFunctionSuggestion(session, signature.name, {
      includeArgumentPlaceholders: true,
      includeClosingParen: true,
    })
  }

  if (assistance?.activeFunction) {
    const includeClosingParen = shouldInsertClosingParen(session.draft, session.caretEnd)
    const replacement = `${args}${includeClosingParen ? ')' : ''}`
    const replaced = replaceText(session.draft, session.caretStart, session.caretEnd, replacement)
    const selectionStart = session.caretStart
    return {
      ...updateFormulaDraft(session, replaced.draft, selectionStart, selectionStart + args.length),
      mode: 'function-assist',
      functionSuggestionIndex: 0,
    }
  }

  return session
}

const applyFormulaAutoSumCommand = (
  session: FormulaEditSession,
  range?: FormulaRangeCoord | null,
): FormulaEditSession => {
  if (range) {
    const replacement = `=SUM(${formatFormulaRangeRef(range)})`
    return {
      ...updateFormulaDraft(session, replacement, replacement.length, replacement.length),
      mode: 'edit',
      functionSuggestionIndex: 0,
    }
  }
  const prefix = isFormulaText(session.draft) ? '' : '='
  const replaced = replaceText(session.draft, session.caretStart, session.caretEnd, `${prefix}SUM(`)
  return {
    ...updateFormulaDraft(session, replaced.draft, replaced.caret, replaced.caret),
    mode: 'point',
    functionSuggestionIndex: 0,
  }
}

const activeReferenceAtCaret = (
  spans: FormulaReferenceSpan[],
  caretStart: number,
  caretEnd: number,
): FormulaReferenceSpan | null => {
  const selected = caretStart !== caretEnd
    ? spans.find((span) => span.startOffset < caretEnd && span.endOffset > caretStart)
    : null
  if (selected) return selected
  return spans.find((span) => caretStart >= span.startOffset && caretStart <= span.endOffset)
    ?? spans.filter((span) => span.endOffset <= caretStart).at(-1)
    ?? null
}

export const isFormulaReferenceContext = (text: string, caretOffset: number): boolean => {
  if (!isFormulaText(text)) return false
  const before = text.slice(0, Math.max(0, caretOffset))
  let inString = false
  for (let index = 0; index < before.length; index += 1) {
    if (before[index] !== '"') continue
    if (before[index + 1] === '"') {
      index += 1
      continue
    }
    inString = !inString
  }
  if (inString) return false
  const previous = before.trimEnd().at(-1)
  return !previous || previous === '=' || previous === '(' || previous === ',' || /[+\-*/^&=<>{}:]/.test(previous)
}

export const createFormulaEditSession = (input: {
  id?: string
  anchorCell: FormulaCellCoord
  sheetName?: string
  text: string
  caretStart?: number
  caretEnd?: number
}): FormulaEditSession => {
  const draft = input.text
  const rawCaretStart = clampFormulaCaretOffset(input.caretStart ?? draft.length, draft)
  const rawCaretEnd = clampFormulaCaretOffset(input.caretEnd ?? rawCaretStart, draft)
  const caretStart = Math.min(rawCaretStart, rawCaretEnd)
  const caretEnd = Math.max(rawCaretStart, rawCaretEnd)
  const analysis = analyzeFormulaAuthoringText(draft)
  const activeReference = activeReferenceAtCaret(analysis.referenceSpans, caretStart, caretEnd)
  const formula = isFormulaText(draft)
  return {
    id: input.id ?? `formula_${input.anchorCell.row}_${input.anchorCell.col}`,
    anchorCell: normalizeCellCoord(input.anchorCell),
    anchorSheetName: normalizeSheetName(input.sheetName),
    activeSheetName: normalizeSheetName(input.sheetName),
    originalText: draft,
    draft,
    caretStart,
    caretEnd,
    mode: formula
      ? (isFormulaReferenceContext(draft, caretEnd) ? 'point' : 'edit')
      : 'value',
    activeReferenceId: activeReference?.id ?? null,
    pointRange: activeReference?.range ?? null,
    referenceSpans: analysis.referenceSpans,
    functionSpans: analysis.functionSpans,
    namedReferenceSpans: analysis.namedReferenceSpans,
    parseError: analysis.parseError,
    functionSuggestionIndex: 0,
    commitNavigation: null,
    status: 'active',
  }
}

export const updateFormulaDraft = (
  session: FormulaEditSession,
  draft: string,
  caretStart = draft.length,
  caretEnd = caretStart,
): FormulaEditSession => {
  const nextCaretStart = Math.min(
    clampFormulaCaretOffset(caretStart, draft),
    clampFormulaCaretOffset(caretEnd, draft),
  )
  const nextCaretEnd = Math.max(
    clampFormulaCaretOffset(caretStart, draft),
    clampFormulaCaretOffset(caretEnd, draft),
  )
  const analysis = analyzeFormulaAuthoringText(draft)
  const activeReference = activeReferenceAtCaret(analysis.referenceSpans, nextCaretStart, nextCaretEnd)
  const formula = isFormulaText(draft)
  const assistance = getFormulaFunctionAssistance(draft, nextCaretEnd, session.functionSuggestionIndex)
  const functionSuggestionIndex = assistance?.suggestions.length
    ? Math.min(session.functionSuggestionIndex, assistance.suggestions.length - 1)
    : 0
  return {
    ...session,
    draft,
    caretStart: nextCaretStart,
    caretEnd: nextCaretEnd,
    mode: formula
      ? (session.mode === 'point' || isFormulaReferenceContext(draft, nextCaretEnd) ? 'point' : 'edit')
      : 'value',
    activeReferenceId: activeReference?.id ?? null,
    pointRange: activeReference?.range ?? session.pointRange,
    referenceSpans: analysis.referenceSpans,
    functionSpans: analysis.functionSpans,
    namedReferenceSpans: analysis.namedReferenceSpans,
    parseError: analysis.parseError,
    functionSuggestionIndex,
    commitNavigation: null,
    status: 'active',
  }
}

export const updateFormulaSelection = (
  session: FormulaEditSession,
  caretStart: number,
  caretEnd = caretStart,
): FormulaEditSession => updateFormulaDraft(session, session.draft, caretStart, caretEnd)

const replaceText = (
  text: string,
  startOffset: number,
  endOffset: number,
  replacement: string,
): { draft: string; caret: number } => ({
  draft: `${text.slice(0, startOffset)}${replacement}${text.slice(endOffset)}`,
  caret: startOffset + replacement.length,
})

export const insertOrReplaceFormulaRange = (
  session: FormulaEditSession,
  range: FormulaRangeCoord,
): FormulaEditSession => {
  const reference = formatFormulaRangeRef(range)
  const active = session.activeReferenceId
    ? session.referenceSpans.find((span) => span.id === session.activeReferenceId) ?? null
    : activeReferenceAtCaret(session.referenceSpans, session.caretStart, session.caretEnd)
  const startOffset = session.caretStart !== session.caretEnd
    ? Math.min(session.caretStart, session.caretEnd)
    : active?.startOffset ?? session.caretStart
  const endOffset = session.caretStart !== session.caretEnd
    ? Math.max(session.caretStart, session.caretEnd)
    : active?.endOffset ?? session.caretEnd
  const replaced = replaceText(session.draft, startOffset, endOffset, reference)
  const next = updateFormulaDraft(session, replaced.draft, replaced.caret, replaced.caret)
  return {
    ...next,
    mode: 'point',
    pointRange: normalizeFormulaRange(range),
  }
}

export const replaceFormulaReferenceRangeById = (
  session: FormulaEditSession,
  referenceId: string,
  range: FormulaRangeCoord,
): FormulaEditSession => {
  const active = session.referenceSpans.find((span) => span.id === referenceId)
  if (!active) return session
  const replacement = formatFormulaRangeRef(range)
  const replaced = replaceText(session.draft, active.startOffset, active.endOffset, replacement)
  const next = updateFormulaDraft(session, replaced.draft, replaced.caret, replaced.caret)
  const nextActive = next.referenceSpans.find((span) => (
    span.startOffset === active.startOffset &&
    span.endOffset === active.startOffset + replacement.length
  ))
  return {
    ...next,
    mode: 'range-resize',
    activeReferenceId: nextActive?.id ?? next.activeReferenceId,
    pointRange: normalizeFormulaRange(range),
  }
}

export const moveFormulaReferenceRangeById = (
  session: FormulaEditSession,
  referenceId: string,
  start: FormulaCellCoord,
): FormulaEditSession => {
  const active = session.referenceSpans.find((span) => span.id === referenceId)
  if (!active?.range) return session
  const current = normalizeFormulaRange(active.range)
  const nextStart = normalizeCellCoord(start)
  const width = Math.max(0, current.end.col - current.start.col)
  const height = Math.max(0, current.end.row - current.start.row)
  return replaceFormulaReferenceRangeById(session, referenceId, {
    sheetName: current.sheetName,
    start: nextStart,
    end: {
      col: nextStart.col + width,
      row: nextStart.row + height,
    },
  })
}

const pointRangeFromSession = (session: FormulaEditSession): FormulaRangeCoord => (
  session.pointRange
  ?? session.referenceSpans.find((span) => span.id === session.activeReferenceId)?.range
  ?? { start: session.anchorCell, end: session.anchorCell }
)

export const moveFormulaPointRange = (
  session: FormulaEditSession,
  direction: FormulaArrowDirection,
  options: { extend?: boolean; jumpToEdge?: boolean; maxRows?: number; maxCols?: number } = {},
): FormulaEditSession => {
  const maxRows = Math.max(1, Math.floor(options.maxRows ?? 1_048_576))
  const maxCols = Math.max(1, Math.floor(options.maxCols ?? 16_384))
  const current = pointRangeFromSession(session)
  const delta = (() => {
    switch (direction) {
      case 'up': return { col: 0, row: -1 }
      case 'down': return { col: 0, row: 1 }
      case 'left': return { col: -1, row: 0 }
      case 'right': return { col: 1, row: 0 }
    }
  })()
  const moveCoord = (coord: FormulaCellCoord): FormulaCellCoord => {
    if (options.jumpToEdge) {
      if (direction === 'up') return { ...coord, row: 0 }
      if (direction === 'down') return { ...coord, row: maxRows - 1 }
      if (direction === 'left') return { ...coord, col: 0 }
      return { ...coord, col: maxCols - 1 }
    }
    return {
      col: Math.min(maxCols - 1, Math.max(0, coord.col + delta.col)),
      row: Math.min(maxRows - 1, Math.max(0, coord.row + delta.row)),
    }
  }

  const nextRange = options.extend
    ? { ...current, end: moveCoord(current.end) }
    : { ...current, start: moveCoord(current.end), end: moveCoord(current.end) }
  return insertOrReplaceFormulaRange(
    { ...session, mode: 'point', pointRange: current },
    nextRange,
  )
}

const nextAbsoluteMode = (mode: FormulaReferenceAbsoluteMode): FormulaReferenceAbsoluteMode => {
  switch (mode) {
    case 'relative': return 'absolute'
    case 'absolute': return 'row-absolute'
    case 'row-absolute': return 'col-absolute'
    case 'col-absolute': return 'relative'
  }
}

const absoluteModeOfCellText = (cellText: string): FormulaReferenceAbsoluteMode => {
  const match = /^(\$?)[A-Za-z]{1,3}(\$?)[1-9][0-9]*$/.exec(cellText)
  if (!match) return 'relative'
  const colAbs = match[1] === '$'
  const rowAbs = match[2] === '$'
  if (colAbs && rowAbs) return 'absolute'
  if (rowAbs) return 'row-absolute'
  if (colAbs) return 'col-absolute'
  return 'relative'
}

const applyModeToCellText = (cellText: string, mode: FormulaReferenceAbsoluteMode): string => {
  const match = /^\$?([A-Za-z]{1,3})\$?([1-9][0-9]*)$/.exec(cellText)
  if (!match) return cellText
  const col = columnIndexFromLetters(match[1])
  const row = Number(match[2]) - 1
  return formatFormulaCellRef({ col, row }, mode)
}

export const cycleFormulaReferenceAbsoluteMode = (referenceText: string): string => {
  const lastBang = referenceText.lastIndexOf('!')
  const prefix = lastBang >= 0 ? referenceText.slice(0, lastBang + 1) : ''
  const body = lastBang >= 0 ? referenceText.slice(lastBang + 1) : referenceText
  const spillSuffix = body.endsWith('#') ? '#' : ''
  const bodyWithoutSpill = spillSuffix ? body.slice(0, -1) : body
  const cells = bodyWithoutSpill.split(':')
  const firstCell = cells.find((part) => /^\$?[A-Za-z]{1,3}\$?[1-9][0-9]*$/.test(part))
  if (!firstCell) return referenceText
  const mode = nextAbsoluteMode(absoluteModeOfCellText(firstCell))
  return `${prefix}${cells.map((part) => applyModeToCellText(part, mode)).join(':')}${spillSuffix}`
}

export const cycleActiveFormulaReferenceAbsoluteMode = (
  session: FormulaEditSession,
): FormulaEditSession => {
  const active = session.activeReferenceId
    ? session.referenceSpans.find((span) => span.id === session.activeReferenceId) ?? null
    : activeReferenceAtCaret(session.referenceSpans, session.caretStart, session.caretEnd)
  if (!active) return session
  const replacement = cycleFormulaReferenceAbsoluteMode(active.text)
  if (replacement === active.text) return session
  const replaced = replaceText(session.draft, active.startOffset, active.endOffset, replacement)
  return updateFormulaDraft(session, replaced.draft, replaced.caret, replaced.caret)
}

export const toggleFormulaPointMode = (session: FormulaEditSession): FormulaEditSession => {
  if (!isFormulaText(session.draft)) return session
  return {
    ...session,
    mode: session.mode === 'point' ? 'edit' : 'point',
  }
}

const formulaCommandDecision = (commandId: string): FormulaKeyCommandDecision => ({
  commandId,
  disposition: 'formula-command',
  preventDefault: true,
  stopPropagation: true,
})

const nativeEditingDecision = (commandId: string): FormulaKeyCommandDecision => ({
  commandId,
  disposition: 'native-editing',
  preventDefault: false,
  stopPropagation: true,
})

const hostSuppressedDecision = (
  commandId: string,
  options: { preventDefault?: boolean } = {},
): FormulaKeyCommandDecision => ({
  commandId,
  disposition: 'host-suppressed',
  preventDefault: options.preventDefault ?? true,
  stopPropagation: true,
})

const unhandledFormulaKeyCommandDecision: FormulaKeyCommandDecision = {
  commandId: 'unhandled',
  disposition: 'unhandled',
  preventDefault: false,
  stopPropagation: false,
}

const primaryModifierPressed = (command: FormulaKeyCommand): boolean => Boolean(command.ctrlKey || command.metaKey)

const normalizedCommandKey = (command: FormulaKeyCommand): string => (
  command.key.length === 1 ? command.key.toLowerCase() : command.key
)

const isSpaceCommand = (command: FormulaKeyCommand): boolean => (
  command.key === ' ' || command.key === 'Spacebar'
)

const formulaNavigationKeys = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'])
const nativeTextEditingKeys = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Backspace',
  'Delete',
])
const hostFormattingShortcutKeys = new Set(['b', 'i', 'u', '1', '5'])
const nativeClipboardShortcutKeys = new Set(['c', 'x', 'v'])
const nativeUndoRedoShortcutKeys = new Set(['z', 'y'])

export const resolveFormulaKeyCommand = (
  session: FormulaEditSession,
  command: FormulaKeyCommand,
  _options: FormulaKeyCommandOptions = {},
): FormulaKeyCommandDecision => {
  if (session.status !== 'active') return unhandledFormulaKeyCommandDecision

  const key = normalizedCommandKey(command)
  const primaryModifier = primaryModifierPressed(command)
  const formulaText = isFormulaText(session.draft)
  const assistance = getFormulaFunctionAssistance(session.draft, session.caretEnd, session.functionSuggestionIndex)
  const hasFunctionAssistTarget = Boolean(
    assistance?.activeFunction || (assistance?.autocompleteQuery && assistance.suggestions.length > 0),
  )

  if (command.key === 'Escape') return formulaCommandDecision('formula-cancel')
  if (command.altKey && command.key === '=') return formulaCommandDecision('formula-autosum')
  if (command.key === 'F3' && command.shiftKey) return formulaCommandDecision('function-assist-open')

  if (key === 'a' && primaryModifier && command.shiftKey) {
    if (hasFunctionAssistTarget) {
      return formulaCommandDecision('function-argument-placeholders')
    }
    return nativeEditingDecision('native-text-selection')
  }

  if (key === 'a' && primaryModifier) {
    if (hasFunctionAssistTarget) {
      return formulaCommandDecision('function-assist-open')
    }
    return nativeEditingDecision('native-select-all')
  }

  if (assistance?.autocompleteQuery && assistance.suggestions.length > 0 && command.key === 'ArrowDown') {
    return formulaCommandDecision('function-suggestion-next')
  }
  if (assistance?.autocompleteQuery && assistance.suggestions.length > 0 && command.key === 'ArrowUp') {
    return formulaCommandDecision('function-suggestion-previous')
  }
  if ((command.key === 'Enter' || command.key === 'Tab') && assistance?.autocompleteQuery && assistance.suggestions.length > 0) {
    return formulaCommandDecision('function-suggestion-accept')
  }
  if (command.key === 'Enter') return formulaCommandDecision('formula-commit-enter')
  if (command.key === 'Tab') return formulaCommandDecision('formula-commit-tab')
  if (command.key === 'F2' && formulaText) return formulaCommandDecision('formula-point-mode-toggle')
  if (command.key === 'F4' && formulaText) return formulaCommandDecision('formula-reference-absolute-toggle')

  if (formulaText && session.mode === 'point') {
    if (command.key === 'ArrowUp') return formulaCommandDecision('point-range-up')
    if (command.key === 'ArrowDown') return formulaCommandDecision('point-range-down')
    if (command.key === 'ArrowLeft') return formulaCommandDecision('point-range-left')
    if (command.key === 'ArrowRight') return formulaCommandDecision('point-range-right')
  }

  if (primaryModifier && command.altKey && key === 'v') {
    return hostSuppressedDecision('host-paste-special')
  }
  if (primaryModifier && nativeClipboardShortcutKeys.has(key)) {
    return nativeEditingDecision(`native-${key === 'c' ? 'copy' : key === 'x' ? 'cut' : 'paste'}`)
  }
  if (primaryModifier && nativeUndoRedoShortcutKeys.has(key)) {
    return nativeEditingDecision(`native-${key === 'z' ? 'undo' : 'redo'}`)
  }
  if (primaryModifier && hostFormattingShortcutKeys.has(key)) {
    return hostSuppressedDecision(`host-format-${key}`)
  }
  if (isSpaceCommand(command) && primaryModifier) {
    return hostSuppressedDecision(command.shiftKey ? 'host-select-all' : 'host-select-column')
  }
  if (isSpaceCommand(command) && command.shiftKey) {
    return hostSuppressedDecision('host-select-row', { preventDefault: false })
  }

  if (nativeTextEditingKeys.has(command.key)) {
    return nativeEditingDecision(formulaNavigationKeys.has(command.key) ? 'native-navigation' : 'native-delete')
  }
  if (isSpaceCommand(command) || command.key.length === 1) {
    return nativeEditingDecision('native-text-input')
  }

  return formulaText || session.mode !== 'value'
    ? nativeEditingDecision('native-editor-key')
    : unhandledFormulaKeyCommandDecision
}

export const applyFormulaKeyCommand = (
  session: FormulaEditSession,
  command: FormulaKeyCommand,
  options: FormulaKeyCommandOptions = {},
): FormulaEditSession => {
  if (session.status !== 'active') return session
  const decision = resolveFormulaKeyCommand(session, command, options)
  const assistance = getFormulaFunctionAssistance(session.draft, session.caretEnd, session.functionSuggestionIndex)
  switch (decision.commandId) {
    case 'formula-cancel':
      return { ...session, draft: session.originalText, status: 'cancelled' }
    case 'formula-autosum':
      return applyFormulaAutoSumCommand(session, options.autoSumRange)
    case 'function-assist-open':
      return { ...session, mode: 'function-assist' }
    case 'function-argument-placeholders':
      return insertFormulaFunctionArgumentPlaceholders(session)
    case 'function-suggestion-next':
      return moveFormulaFunctionSuggestion(session, 'next')
    case 'function-suggestion-previous':
      return moveFormulaFunctionSuggestion(session, 'previous')
    case 'function-suggestion-accept':
      return assistance?.autocompleteQuery && assistance.suggestions.length > 0
        ? insertFormulaFunctionSuggestion(session, assistance.suggestions[assistance.activeSuggestionIndex].name)
        : session
    case 'formula-commit-enter':
      return { ...session, status: 'committed', commitNavigation: command.shiftKey ? 'up' : 'down' }
    case 'formula-commit-tab':
      return { ...session, status: 'committed', commitNavigation: command.shiftKey ? 'left' : 'right' }
    case 'formula-point-mode-toggle':
      return toggleFormulaPointMode(session)
    case 'formula-reference-absolute-toggle':
      return cycleActiveFormulaReferenceAbsoluteMode(session)
    case 'point-range-up':
      return moveFormulaPointRange(session, 'up', { extend: command.shiftKey, jumpToEdge: command.ctrlKey || command.metaKey })
    case 'point-range-down':
      return moveFormulaPointRange(session, 'down', { extend: command.shiftKey, jumpToEdge: command.ctrlKey || command.metaKey })
    case 'point-range-left':
      return moveFormulaPointRange(session, 'left', { extend: command.shiftKey, jumpToEdge: command.ctrlKey || command.metaKey })
    case 'point-range-right':
      return moveFormulaPointRange(session, 'right', { extend: command.shiftKey, jumpToEdge: command.ctrlKey || command.metaKey })
    default:
      return session
  }
}
